/**
 * ポスト投稿 UI（フェーズ3・要件23-8・画像のみ）。
 *
 * ログイン中のみ表示。本文＋画像（任意）で、現在の最新位置にポストを作成する。
 * Telegram の `/post` と同じデータモデル・マップ表示・共有ページを共通利用する。
 * - 画像はブラウザで長辺1280pxにリサイズして送信（base64）。
 * - 位置が無い（未チェックイン/失効）場合はサーバーが 409 を返すので、チェックインを促す。
 * - 動画は将来対応（本 UI では画像のみ）。
 */
import { useState } from 'react';
import {
  createPost,
  getMyProfile,
  getUploadUrls,
  postToPresignedForm,
} from '../api/client';
import { resizeImageToDataUrl } from '../map/imageResize';
import { generateVideoThumbnail } from '../map/videoThumb';
import { nearestVenue } from '../map/venues';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { useMenuModal } from './userMenuContext';
import { useGeolocationPermission } from './useGeolocationPermission';
import './postControl.css';

/** 本文の長さ上限（サーバー側 BODY_MAX と一致させる）。 */
const BODY_MAX = 500;
/**
 * Web 投稿の動画上限サイズ（150MB・サーバーの MAX_VIDEO_UPLOAD_BYTES と同じ値）。
 * Web はブラウザから S3 へプリサインド POST で直接上げるため API Gateway/Lambda の
 * ペイロード制限を受けず、上限を大きく取れる。スマホの既定の 4K 撮影でも短い動画が通るようにする
 * （4K・30fps で約50秒、4K・60fps で約20秒・タスク55.7）。
 * （Telegram 経路は Bot API getFile の 20MB 固定で別。telegramWebhook 側は変更しない）
 */
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;

export interface PostControlProps {
  /** App で一度だけ呼んだ useAuth の結果を渡す。 */
  readonly auth: UseAuthResult;
  /** 投稿成功時に呼ばれる（マップのポストを即リフレッシュする）。 */
  readonly onPosted?: () => void;
  /** トリガーボタンの表示形態（menu=メニュー項目／floating=旧フロート）。 */
  readonly variant?: 'menu' | 'floating';
  readonly lang: Lang;
}

export function PostControl({
  auth,
  onPosted,
  variant = 'menu',
  lang,
}: PostControlProps) {
  const { enabled, user, loading } = auth;
  const loggedIn = enabled && !loading && !!user;
  const m = messages(lang);

  const [open, setOpen] = useMenuModal();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [body, setBody] = useState('');
  /** リサイズ済みの画像 data URL（プレビュー兼送信用）。 */
  const [imageData, setImageData] = useState<string | null>(null);
  /** 選択中の動画ファイル（直 PUT 用）。 */
  const [videoFile, setVideoFile] = useState<File | null>(null);
  /** 動画プレビュー用のオブジェクト URL。 */
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  /** 取得できた現在地（投稿位置に使う）。null は未取得＝チェックイン位置にフォールバック。 */
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  /** 現在地の取得状態（UI の位置ラベル表示に使う）。 */
  const [locating, setLocating] = useState(false);
  /** 現在地が取れなかった理由（タスク46）。取れた・取得中・未試行は null。 */
  const [locError, setLocError] = useState<
    'denied' | 'unavailable' | 'timeout' | 'unsupported' | null
  >(null);
  /** 「現在地を取り直す」で取り直したか（再失敗を初回の失敗と区別して伝える）。 */
  const [locRetried, setLocRetried] = useState(false);
  /** ブロックのまま「現在地を取り直す」が押された（押したことが分かるように表示を変える）。 */
  const [locStillDenied, setLocStillDenied] = useState(false);
  /**
   * 期限内のチェックイン位置（タスク47）。あれば現在地が取れなくてもそこに投稿できるので、現在地の注意は出さない。
   * 取得中は 'loading'（注意を出さない＝ちらつき防止）、無い・取得失敗は 'none'（注意を出す＝安全側）。
   */
  const [checkin, setCheckin] = useState<
    'loading' | 'none' | { lat: number; lng: number }
  >('loading');

  function idToken(): string | null {
    return loadTokens()?.id_token ?? null;
  }

  function clearMedia(): void {
    setImageData(null);
    setVideoFile(null);
    setVideoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }

  // モーダルが開いている間、位置情報の許可状態を見張る（タスク46.4・共通フックはタスク49）。
  // サイト設定で「許可」に変わったら、ボタンを押さなくても自動で取り直す。
  const locPermission = useGeolocationPermission(open, () => {
    setLocStillDenied(false);
    fetchCurrentLocation();
  });

  /** 「現在地を取り直す」。ブロック中は呼んでも即拒否されるだけなので、押したことが分かる表示に切り替える。 */
  function retryLocation(): void {
    if (locPermission === 'denied') {
      setLocStillDenied(true);
      return;
    }
    setLocRetried(true);
    fetchCurrentLocation();
  }

  /**
   * 現在地を取得する（取れたら coords に設定・乖離対策）。失敗時は null のまま。
   * 取り直し中は前回の理由を消さずに残す（理由欄ごと消えて「取得中…」が見えなくなるのを防ぐ・タスク46.4）。
   */
  function fetchCurrentLocation(): void {
    if (!navigator.geolocation) {
      setLocError('unsupported');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocError(null);
        setLocRetried(false);
        setLocating(false);
      },
      (err) => {
        // 取得失敗 → チェックイン位置にフォールバック。理由を出し分けて取り直せるようにする（タスク46）。
        // 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT（CheckinControl と同じ分類）
        console.warn('post geolocation failed', err.code, err.message);
        setCoords(null);
        setLocError(
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.TIMEOUT
              ? 'timeout'
              : 'unavailable',
        );
        setLocating(false);
      },
      // チェックインと同じ緩い設定（高精度オフ・キャッシュ許容）で成功率を上げる。
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
    );
  }

  function openModal(): void {
    setBody('');
    clearMedia();
    setCoords(null);
    setLocError(null);
    setLocRetried(false);
    setLocStillDenied(false);
    setCheckin('loading');
    setMsg(null);
    setOpen(true);
    // 開いたら現在地取得を試みる（許可済みなら即・乖離対策）。
    fetchCurrentLocation();
    // 並行して期限内のチェックイン位置を取る（タスク47）
    void loadCheckin();
  }

  async function loadCheckin(): Promise<void> {
    const token = idToken();
    if (!token) {
      setCheckin('none');
      return;
    }
    try {
      const profile = await getMyProfile(token);
      setCheckin(
        profile.checkin
          ? { lat: profile.checkin.lat, lng: profile.checkin.lng }
          : 'none',
      );
    } catch (err) {
      console.error('checkin lookup failed', err);
      setCheckin('none');
    }
  }

  async function handlePickImage(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setMsg(null);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      // 画像と動画は排他（片方を選んだらもう片方は外す）
      setVideoFile(null);
      setVideoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setImageData(dataUrl);
    } catch {
      setMsg(m.post.imageLoadFailed);
    } finally {
      e.target.value = '';
    }
  }

  function handlePickVideo(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setMsg(null);
    if (file.size > MAX_VIDEO_BYTES) {
      setMsg(m.post.videoTooLarge);
      e.target.value = '';
      return;
    }
    // 画像と動画は排他
    setImageData(null);
    setVideoFile(file);
    setVideoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    e.target.value = '';
  }

  function handleSubmitError(err: unknown): void {
    // 位置が無い（未チェックイン/失効）→ サーバーは 409
    if (err instanceof Error && err.message.includes('409')) {
      setMsg(m.post.needLocation);
    } else {
      setMsg(m.post.failed);
    }
  }

  async function handleSubmit(): Promise<void> {
    const token = idToken();
    if (!token) {
      setMsg(m.post.sessionExpired);
      return;
    }
    const text = body.trim();
    if (text.length === 0 && !imageData && !videoFile) {
      setMsg(m.post.empty);
      return;
    }
    if (Array.from(text).length > BODY_MAX) {
      setMsg(m.post.bodyTooLong(BODY_MAX));
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      if (videoFile) {
        // 動画投稿: upload-url 取得 → S3 直 PUT（動画＋サムネ）→ createPost で確定
        setMsg(m.post.uploading);
        const urls = await getUploadUrls(token, coords);
        await postToPresignedForm(urls.video_upload, videoFile);
        // サムネはブラウザ生成（失敗しても動画投稿は続行＝🎬 表示にフォールバック）
        const thumb = await generateVideoThumbnail(videoFile);
        if (thumb) {
          try {
            await postToPresignedForm(urls.thumb_upload, thumb);
          } catch {
            // サムネ PUT 失敗は無視（サムネ無しで続行）
          }
        }
        await createPost(token, {
          body: text || undefined,
          post_id: urls.post_id,
          media_type: 'video',
          lat: coords?.lat,
          lng: coords?.lng,
        });
      } else {
        await createPost(token, {
          body: text || undefined,
          image: imageData ?? undefined,
          lat: coords?.lat,
          lng: coords?.lng,
        });
      }
      onPosted?.();
      setMsg(m.post.posted);
      setTimeout(() => setOpen(false), 1500);
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!loggedIn) {
    return null;
  }

  // 投稿位置ラベル（乖離対策）: 現在地が取れていれば「現在地（◯◯付近）」、
  // 取れていなければ「最新のチェックイン位置」。会場名はしきい値内のみ付ける。
  let locationLabel: string;
  if (locating) {
    locationLabel = m.post.locating;
  } else if (coords) {
    const venue = nearestVenue(coords.lat, coords.lng);
    const name = venue ? (lang === 'ja' ? venue.name : venue.nameEn) : null;
    locationLabel = name
      ? m.post.postAtCurrentNear(name)
      : m.post.postAtCurrent;
  } else if (typeof checkin === 'object') {
    // 現在地は取れないが期限内のチェックインがある → その位置に投稿される（タスク47）
    const venue = nearestVenue(checkin.lat, checkin.lng);
    const name = venue ? (lang === 'ja' ? venue.name : venue.nameEn) : null;
    locationLabel = name ? m.post.postAtCheckinNear(name) : m.post.postAtCheckin;
  } else {
    // 現在地もチェックインも無い（または確認中）→ サーバーのチェックイン位置判定に任せる
    locationLabel = m.post.postAtCheckin;
  }
  const locErrorText: Record<NonNullable<typeof locError>, string> = {
    denied: m.post.locDenied,
    unavailable: m.post.locUnavailable,
    timeout: m.post.locTimeout,
    unsupported: m.post.locUnsupported,
  };

  return (
    <>
      <button
        type="button"
        className={variant === 'menu' ? 'user-menu__item' : 'post-trigger'}
        onClick={openModal}
      >
        {m.post.trigger}
      </button>

      {open && (
        <div className="post-modal__overlay" onClick={() => setOpen(false)}>
          <div
            className="post-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={m.post.title}
          >
            <h2 className="post-modal__title">{m.post.title}</h2>
            <p className="post-modal__note">{m.post.note}</p>
            <p className="post-modal__location">{locationLabel}</p>
            {/* 現在地の注意は、期限内のチェックインが無いと分かったときだけ出す（タスク47）。
                チェックインがあればそこに投稿できるので、位置情報を共有しない人に許可を促さない。 */}
            {locError && checkin === 'none' && (
              <div className="post-modal__loc-error">
                <p>{locErrorText[locError]}</p>
                {/* 押したことが分かるように、再失敗・ブロック継続をはっきり出す（タスク46.4） */}
                {locStillDenied && (
                  <p className="post-modal__loc-again">{m.post.locStillDenied}</p>
                )}
                {!locStillDenied && locRetried && !locating && (
                  <p className="post-modal__loc-again">{m.post.locRetryFailed}</p>
                )}
                {locError !== 'unsupported' && (
                  <div className="post-modal__loc-actions">
                    <button
                      type="button"
                      className="post-modal__loc-retry"
                      onClick={retryLocation}
                      disabled={busy || locating}
                    >
                      {locating ? m.post.locRetrying : m.post.retryLocation}
                    </button>
                    {locError === 'denied' && (
                      <button
                        type="button"
                        className="post-modal__loc-retry"
                        onClick={() => window.location.reload()}
                        disabled={busy}
                      >
                        {m.post.reloadPage}
                      </button>
                    )}
                  </div>
                )}
                {locError === 'denied' && (
                  <p className="post-modal__loc-note">{m.post.reloadNote}</p>
                )}
              </div>
            )}

            <textarea
              className="post-modal__body"
              value={body}
              maxLength={BODY_MAX}
              rows={3}
              onChange={(e) => setBody(e.target.value)}
              disabled={busy}
              placeholder={m.post.bodyPlaceholder}
            />
            <span className="post-modal__hint">
              {Array.from(body.trim()).length}/{BODY_MAX}
            </span>

            <div className="post-modal__pickers">
              <label
                className={`post-modal__picker${imageData ? ' post-modal__picker--selected' : ''}`}
              >
                <span className="post-modal__picker-icon">📷</span>
                <span>{imageData ? m.post.reselectImage : m.post.addImage}</span>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => void handlePickImage(e)}
                  disabled={busy}
                />
              </label>

              <label
                className={`post-modal__picker${videoFile ? ' post-modal__picker--selected' : ''}`}
              >
                <span className="post-modal__picker-icon">🎬</span>
                <span>{videoFile ? m.post.reselectVideo : m.post.addVideo}</span>
                <span className="post-modal__picker-hint">{m.post.sizeLimit}</span>
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={(e) => handlePickVideo(e)}
                  disabled={busy}
                />
              </label>
            </div>

            {imageData && (
              <div className="post-modal__preview">
                <img src={imageData} alt={m.post.removeImage} />
                <button
                  type="button"
                  className="post-modal__preview-remove"
                  onClick={() => setImageData(null)}
                  disabled={busy}
                  aria-label={m.post.removeImage}
                  title={m.post.removeImage}
                >
                  ×
                </button>
              </div>
            )}

            {videoPreview && (
              <div className="post-modal__preview">
                <video src={videoPreview} controls playsInline />
                <button
                  type="button"
                  className="post-modal__preview-remove"
                  onClick={clearMedia}
                  disabled={busy}
                  aria-label={m.post.removeVideo}
                  title={m.post.removeVideo}
                >
                  ×
                </button>
              </div>
            )}

            {msg && <p className="post-modal__msg">{msg}</p>}

            <button
              type="button"
              className="post-modal__submit"
              onClick={() => void handleSubmit()}
              // 現在地の取得中に送ると、意図せずチェックイン位置に投稿されるので待たせる（タスク46）
              disabled={busy || locating}
            >
              {m.post.submit}
            </button>
            <button
              type="button"
              className="post-modal__close"
              onClick={() => setOpen(false)}
            >
              {m.common.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
